import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { Icon } from "@iconify/react";
import { useSnackbar } from "notistack";

import Loader from "@jumbo/components/Loader";
import { ASSET_IMAGES } from "app/utils/constants/paths";

const logoBox = {
  width: 28,
  height: 28,
  borderRadius: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "background.default",
  border: "1px solid",
  borderColor: "divider",
  flexShrink: 0,
};

// Same marks the sidebar uses for these platforms, so they read as the same
// thing wherever they appear.
const PLATFORMS = [
  {
    key: "meesho",
    label: "Meesho",
    logo: <img src={`${ASSET_IMAGES}/meesho.svg`} alt="" width={18} />,
  },
  {
    key: "flipkart",
    label: "Flipkart",
    logo: <Icon icon="simple-icons:flipkart" width={16} color="#2874F0" />,
  },
  {
    key: "myntra",
    label: "Myntra",
    logo: <img src={`${ASSET_IMAGES}/myntra-logo.svg`} alt="" width={18} />,
  },
  {
    key: "amazon",
    label: "Amazon",
    logo: <Icon icon="simple-icons:amazon" width={16} color="#FF9900" />,
  },
  {
    key: "other",
    label: "Other",
    logo: <StorefrontIcon sx={{ fontSize: 17, color: "text.secondary" }} />,
  },
];

/**
 * Puts text on the clipboard.
 *
 * The Clipboard API only exists on https or localhost; served over plain http
 * on the office network it is simply undefined. The hidden-textarea route
 * still works there, so it is kept as the fallback rather than failing.
 */
const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // fall through to the older route
    }
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch (error) {
    copied = false;
  }
  document.body.removeChild(area);
  return copied;
};

/**
 * One SKU. Double-click copies it.
 *
 * Text selection is switched off on purpose: a SKU is full of hyphens, and a
 * browser's own double-click would highlight just one piece of it - the
 * screen would then show part of the code selected while the whole of it had
 * been copied. Enter copies too, for keyboard use.
 */
const SkuItem = ({ sku, copied, onCopy }) => (
  <Tooltip title={copied ? "Copied" : "Double-click to copy"} placement="left">
    <Box
      role="button"
      tabIndex={0}
      onDoubleClick={() => onCopy(sku)}
      onKeyDown={(event) => {
        if (event.key === "Enter") onCopy(sku);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: "1px solid",
        borderColor: copied ? "success.main" : "divider",
        bgcolor: copied ? "rgba(46, 125, 50, 0.06)" : "background.paper",
        cursor: "copy",
        userSelect: "none",
        transition: "background-color 120ms, border-color 120ms",
        "&:hover": {
          bgcolor: copied ? "rgba(46, 125, 50, 0.06)" : "action.hover",
          borderColor: copied ? "success.main" : "primary.light",
        },
        "&:hover .sku-copy-hint": { opacity: 1 },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 1,
        },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 14,
          letterSpacing: 0.2,
          wordBreak: "break-all",
        }}
      >
        {sku}
      </Typography>
      {copied ? (
        <CheckIcon
          sx={{ fontSize: 16, color: "success.main", flexShrink: 0 }}
        />
      ) : (
        <ContentCopyIcon
          className="sku-copy-hint"
          sx={{
            fontSize: 15,
            color: "text.secondary",
            opacity: 0,
            transition: "opacity 120ms",
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  </Tooltip>
);

const PlatformSection = ({ platform, skus, copiedSku, onCopy }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
      <Box sx={logoBox}>{platform.logo}</Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {platform.label}
      </Typography>
      <Chip
        size="small"
        label={skus.length}
        sx={{ height: 20, fontSize: 12, fontWeight: 600 }}
      />
    </Stack>
    <Stack spacing={0.75}>
      {skus.map((sku) => (
        <SkuItem
          key={sku}
          sku={sku}
          copied={copiedSku === `${platform.key}|${sku}`}
          onCopy={(value) => onCopy(platform.key, value)}
        />
      ))}
    </Stack>
  </Box>
);

/**
 * Every SKU the chosen variant sells under, one section per platform.
 *
 * Platforms that have codes come first, in a fixed order; the ones without any
 * are named together at the bottom rather than each taking a block of space to
 * say it is empty.
 */
const VariantSkuDrawer = ({ open, row, state, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [copiedSku, setCopiedSku] = useState("");
  const timer = useRef(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  // A different variant starts with nothing marked as copied.
  useEffect(() => {
    setCopiedSku("");
  }, [row]);

  const handleCopy = async (platformKey, sku) => {
    const copied = await copyText(sku);
    if (!copied) {
      enqueueSnackbar("Could not copy - the browser blocked the clipboard.", {
        variant: "error",
      });
      return;
    }
    setCopiedSku(`${platformKey}|${sku}`);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopiedSku(""), 1600);
    enqueueSnackbar(`Copied ${sku}`, {
      variant: "success",
      autoHideDuration: 1500,
    });
  };

  const { loading, data, error } = state ?? {};
  const variant = data?.variant;
  const skus = data?.skus ?? {};

  const withSkus = PLATFORMS.filter((p) => (skus[p.key] ?? []).length);
  const withoutSkus = PLATFORMS.filter((p) => !(skus[p.key] ?? []).length);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 440 } } }}
    >
      {/* ---- header ---- */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
            >
              Variant SKUs
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, lineHeight: 1.3, wordBreak: "break-word" }}
            >
              {variant?.name ?? row?.variantName ?? "Variant"}
            </Typography>
            {(variant?.productName || variant?.variantCode) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                {[
                  variant?.productName,
                  variant?.variantCode !== variant?.name
                    ? variant?.variantCode
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{ alignSelf: "flex-start", mr: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        {variant && (
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75, mt: 1.5 }}>
            {variant.size && (
              <Chip
                size="small"
                variant="outlined"
                label={`Size ${variant.size}`}
              />
            )}
            {variant.color && (
              <Chip size="small" variant="outlined" label={variant.color} />
            )}
            <Chip
              size="small"
              color="primary"
              label={`${data.total} SKU${data.total === 1 ? "" : "s"}`}
            />
          </Stack>
        )}
      </Box>

      {/* ---- body ---- */}
      <Box sx={{ px: 3, py: 2.5, overflowY: "auto", flex: 1 }}>
        {loading && <Loader />}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && variant && (
          <>
            {data.total > 0 && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{ mb: 2.5, color: "text.secondary" }}
              >
                <ContentCopyIcon sx={{ fontSize: 15 }} />
                <Typography variant="caption">
                  Double-click any SKU to copy it
                </Typography>
              </Stack>
            )}

            {data.total === 0 ? (
              <Alert severity="info">
                No SKU has been added to this variant on any platform yet.
              </Alert>
            ) : (
              <Stack spacing={2.5} divider={<Divider flexItem />}>
                {withSkus.map((platform) => (
                  <PlatformSection
                    key={platform.key}
                    platform={platform}
                    skus={skus[platform.key]}
                    copiedSku={copiedSku}
                    onCopy={handleCopy}
                  />
                ))}
              </Stack>
            )}

            {data.total > 0 && withoutSkus.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 3 }}
              >
                No SKU on {withoutSkus.map((p) => p.label).join(", ")}.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default VariantSkuDrawer;
